import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import MobileNav from "../../components/mobileNav/MobileNav";
import PrintingHeader from "../../components/printingHeader/PrintingHeader";
import "./technicalScreen.css";
import printIcon from "../../assets/print-icon.svg";

const TechnicalScreen = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [technicianInfo, setTechnicianInfo] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const getAllAppointments = async () => {
    try {
      setError(null);
      setLoading(true);
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      if (!maintenanceAreaIdsString)
        throw new Error("No maintenance area IDs found.");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);

      const url =
        "https://api.generalhome.sa/api/maintenance/GetTechniciansNotApproved";
      const response = await axios.post(url, {
        maintenanceAreaIds: maintenanceAreaIds,
      });
      setData(response.data);
    } catch (err) {
      console.error(err);
      setError("حدث خطأ أثناء تحميل بيانات الحسابات المعلقة.");
    } finally {
      setLoading(false);
    }
  };

  const accountApproval = async (item) => {
    try {
      const technicianId = item.TechnicianId;
      await axios.post(
        "https://api.generalhome.sa/api/maintenance/ApprovingAccountsForTechnicians",
        {
          TechnicianId: `${technicianId}`,
        }
      );
      toast.success("تم قبول الحساب بنجاح");
      setData(data.filter((d) => d.TechnicianId !== technicianId));
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء قبول الحساب");
    }
  };

  const rejectAccount = async (item) => {
    try {
      const technicianId = item.TechnicianId;
      await axios.post(
        "https://api.generalhome.sa/api/maintenance/RejectingTechniciansAccounts",
        {
          TechnicianId: `${technicianId}`,
        }
      );
      toast.success("تم رفض الحساب");
      setData(data.filter((d) => d.TechnicianId !== technicianId));
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء رفض الحساب");
    }
  };

  const GetTechnicianInfo = async () => {
    try {
      setError(null);
      setLoading(true);
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      if (!maintenanceAreaIdsString)
        throw new Error("No maintenance area IDs found.");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);

      const url = "https://api.generalhome.sa/api/Reports/TechnicianReport";
      const response = await axios.post(url, {
        maintenanceAreaIds: maintenanceAreaIds,
        FromDate: fromDate,
        ToDate: toDate,
      });
      setTechnicianInfo(response.data);
    } catch (err) {
      console.error(err);
      setError("حدث خطأ أثناء تحميل بيانات مسئولين الصيانة.");
    } finally {
      setLoading(false);
    }
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
    onAfterPrint: () => console.log("print success"),
  });

  const handleSearch = async (e) => {
    e.preventDefault();
    await GetTechnicianInfo();
  };

  const handleRetry = () => {
    setError(null);
    getAllAppointments();
    GetTechnicianInfo();
  };

  useEffect(() => {
    getAllAppointments();
    GetTechnicianInfo();
  }, []);

  return (
    <>
      <MobileNav />
      <section className="technicalScreen mt-0">
        <Helmet>
          <meta charSet="utf-8" />
          <title> مسئولين الصيانه </title>
          <link
            rel="canonical"
            href="https://generalhome.sa/dashboard/technicalScreen"
          />
        </Helmet>

        {loading ? (
          <div className="spinner-container">
            <ClipLoader
              className="loader"
              loading={loading}
              size={100}
              aria-label="Loading Spinner"
              data-testid="loader"
              color="#00b1ff"
            />
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-card">
              <h2>حدث خطأ ما 😢</h2>
              <p>{error}</p>
              <button onClick={handleRetry}>🔄 إعادة المحاولة</button>
            </div>
          </div>
        ) : (
          <div className="technicalScreen-container">
            <div className="head d-flex justify-content-between">
              <h2>بيانات مسئولين الصيانه</h2>
              <div className="print-icon-holder">
                <img
                  src={printIcon}
                  alt="printIcon"
                  className="print-icon"
                  onClick={handlePrint}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="form-holder">
              <h5>ما الذي تبحث عنه؟</h5>
              <form onSubmit={handleSearch} className="mt-4">
                <h5>من</h5>
                <input
                  type="date"
                  onChange={(e) => setFromDate(e.target.value)}
                  value={fromDate || ""}
                />
                <h5>إلى</h5>
                <input
                  type="date"
                  onChange={(e) => setToDate(e.target.value)}
                  value={toDate || ""}
                />
                <button disabled={loading} type="submit">
                  {loading ? "جاري البحث..." : "بحث"}
                </button>
              </form>
            </div>

            {/* Table */}
            <div
              className="table-responsive-sm overflow-auto"
              ref={componentRef}
            >
              <PrintingHeader
                title="بيانات مسئولين الصيانه"
                count={technicianInfo.length}
              />
              {(fromDate || toDate) && (
                <div className="date-range-display">
                  <div className="date-range-item">
                    <span className="date-label">من</span>
                    <span className="date-value">{fromDate || "--"}</span>
                  </div>
                  <div className="date-range-item">
                    <span className="date-label">إلى</span>
                    <span className="date-value">{toDate || "--"}</span>
                  </div>
                </div>
              )}

              <table className="table table-striped">
                <thead>
                  <tr>
                    <th className="techTh">الإسم</th>
                    <th className="text-center techTh">طلبات قيد التنفيذ</th>
                    <th className="text-center techTh">طلبات تم تنفيذها</th>
                    <th className="text-center techTh">التحكم</th>
                  </tr>
                </thead>
                <tbody>
                  {technicianInfo.map((item) => (
                    <tr key={item.TechnicianId}>
                      <td className="techTd">
                        <Link
                          to={`/dashboard/techStatics/${item.TechnicianId}`}
                        >
                          {item.TechnicianName}
                        </Link>
                      </td>
                      <td className="text-center techTd">
                        {item.NumberOfScheduledProjects}
                      </td>
                      <td className="text-center techTd">
                        {item.NumberOfDoneProjects}
                      </td>
                      <td className="text-center techTd d-flex justify-content-center buttons-box">
                        <Link
                          className="link"
                          to={`/dashboard/scheduledProjects/${item.TechnicianId}`}
                        >
                          <button>طلبات قيد التنفيذ</button>
                        </Link>
                        <Link
                          className="link"
                          to={`/dashboard/doneProjects/${item.TechnicianId}`}
                        >
                          <button>طلبات تم تنفيذها</button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="tech-signiture">
                <h5>توقيع المسؤول</h5>
                <p>....................................</p>
              </div>
            </div>

            {/* Pending Accounts */}
            <div className="pendingAcc pt-5">
              <h2>الحسابات المعلقه :</h2>
              <div className="table-responsive-sm overflow-auto">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>الاسم</th>
                      <th>رقم الهاتف</th>
                      <th className="text-center">التحكم</th>
                    </tr>
                  </thead>
                  {data?.length > 0 ? (
                    <tbody>
                      {data.map((item) => (
                        <tr key={item.TechnicianId}>
                          <td>{item.Username}</td>
                          <td>
                            <a
                              href={`https://api.whatsapp.com/send?phone=+966${item.Mobile}`}
                              target="__blank"
                              rel="noreferrer"
                            >
                              {item.Mobile}
                            </a>
                          </td>
                          <td className="btn-box">
                            <button onClick={() => accountApproval(item)}>
                              قبول الحساب
                            </button>
                            <button onClick={() => rejectAccount(item)}>
                              رفض الحساب
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr className="no-data-message">
                        <td colSpan="3">لا توجد حسابات معلقه :(</td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default TechnicalScreen;
